import { Link } from "react-router-dom";
import RevealSection from "@/components/base/RevealSection";

const KATIA_PHOTO =
  "https://storage.readdy-site.link/project_files/354b6cde-f378-42cf-a9dc-2356d64fa0ef/33d99155-3520-47fa-8e0e-908480cfb870_Katia-headshot-with-border.png?v=b26199f2a021844d0b4ad94738d8e563";

const VALUES = [
  {
    icon: "ri-tools-line",
    title: "Practical",
    desc: "Focused on real workplace conversations, not vague theory.",
  },
  {
    icon: "ri-mental-health-line",
    title: "Calm",
    desc: "Helping people communicate clearly without unnecessary escalation.",
  },
  {
    icon: "ri-briefcase-line",
    title: "Professional",
    desc: "Supporting better outcomes where trust, clarity and emotional awareness matter.",
  },
];

export default function About() {
  return (
    <div className="bg-white">
      {/* Hero / Bio Section */}
      <RevealSection>
        <section className="px-4 md:px-8 lg:px-12 pt-12 md:pt-20 pb-14 md:pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
              {/* Photo */}
              <div className="w-full lg:w-[42%] shrink-0">
                <div className="rounded-xl overflow-hidden bg-seafoam/10">
                  <img
                    src={KATIA_PHOTO}
                    alt="Katia Hüntemann Matas — Workplace Communication Coach"
                    className="w-full h-auto object-cover"
                    style={{ maxHeight: "520px" }}
                  />
                </div>

                <div className="mt-6 space-y-4">
                  <div className="bg-seafoam/20 border border-seafoam/50 rounded-2xl p-5 md:p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal mb-2">
                      Mission
                    </p>
                    <p className="text-navy font-semibold leading-relaxed">
                      Communicate clearly and with confidence. Strategic communication for difficult moments.
                    </p>
                  </div>
                  <div className="bg-white border border-seafoam/50 rounded-2xl p-5 md:p-6 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal mb-2">
                      Vision
                    </p>
                    <p className="text-navy font-semibold leading-relaxed">
                      Enhance business and academic relationships and promote a psychologically safe environment.
                    </p>
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="w-full lg:w-[58%] flex flex-col">
                <span className="text-teal text-sm font-semibold uppercase tracking-[2px] mb-3">
                  About Katia Hüntemann Matas
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-[42px] font-bold text-navy ca-page-title mb-6">
                  Helping professionals handle difficult workplace conversations with
                  more clarity, calm and confidence.
                </h1>

                <div className="space-y-5 text-navy/80 text-base md:text-lg leading-[1.75]">
                  <p>
                    Katia Hüntemann Matas helps professionals navigate difficult workplace
                    conversations with greater clarity, calm and confidence.
                  </p>
                  <p>
                    She is a Neuroscience Coach, Certified by Naturalistico.
                  </p>
                  <p>
                    Her work combines practical workplace communication strategies
                    with ongoing study in emotional intelligence, positive
                    psychology and mindfulness. She has completed education in
                    Emotional Intelligence based on the work of Daniel Goleman and
                    continues to deepen her knowledge through further study with IEPP
                    in positive psychology, emotional intelligence and mindfulness.
                  </p>
                  <p>
                    Rather than relying on scripted corporate communication or
                    overly theoretical approaches, Katia focuses on helping people
                    handle real-world situations professionally and constructively —
                    including feedback conversations, workplace tension,
                    boundary-setting, conflict, repair and emotionally charged
                    discussions.
                  </p>
                  <p>
                    Her approach is calm, practical and grounded in the belief that
                    difficult conversations do not need to become damaging
                    conversations. With the right preparation, structure and awareness,
                    people can communicate more clearly, protect working
                    relationships and reduce unnecessary stress and escalation.
                  </p>
                  <p>
                    Calm Authority was created to help professionals develop those
                    skills in a way that feels realistic, professional and immediately
                    usable in everyday work situations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Values Cards */}
      <RevealSection delay={100}>
        <section className="bg-seafoam/10 px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
              {VALUES.map((card) => (
                <div
                  key={card.title}
                  className="bg-white rounded-xl p-7 md:p-10 border border-seafoam/30"
                >
                  <div className="w-12 h-12 rounded-full bg-seafoam/20 flex items-center justify-center mb-5">
                    <i className={`${card.icon} text-teal text-xl`} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-navy mb-3">
                    {card.title}
                  </h3>
                  <p className="text-navy/70 text-base leading-relaxed">
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* Closing Quote */}
      <RevealSection delay={100}>
        <section className="bg-white px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <div className="border-l-4 border-teal pl-6 text-left md:text-center md:border-l-0 md:pl-0 md:border-t-2 md:pt-6">
              <p className="text-navy/80 text-lg md:text-xl lg:text-[22px] leading-[1.8] font-light italic">
                &ldquo;To care for our planet, we first need to care for each other. Respect is the essential core value that should guide how we communicate, work and live.&rdquo;
              </p>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* CTA */}
      <RevealSection delay={100}>
        <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl lg:text-[34px] font-bold text-navy mb-4 ca-page-title">
              Want to discover your conversation style?
            </h2>
            <p className="text-navy/70 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Take a short quiz to find out where you stand and what you can improve
              next.
            </p>
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center bg-teal text-white font-semibold px-8 py-4 rounded-lg hover:bg-navy transition-colors text-base whitespace-nowrap"
            >
              Take the Free Quiz
            </Link>
          </div>
        </section>
      </RevealSection>
    </div>
  );
}